/** @jsxImportSource @emotion/react */

import { useState } from "react";
import { css } from "@emotion/react";
import { LoginInfo } from "models/user.model";
import { sendLoginRequest } from "pages/remotes";
import { PALETTE } from "styles/base/variables";
import BaseInput from "components/common/Input/Base";
import BaseButton from "components/common/Button/Base";
import { useNavigate } from "react-router-dom";
import BannerBg from "assets/img/banner/banner-bg.png";
import LoginIntro from "assets/img/illust/login-intro.png";
import { ReactComponent as LogoSeteukPro } from "assets/img/logo/logo-seteuk-pro.svg";
import { ReactComponent as BannerCircle } from "assets/img/banner/banner-circle.svg";
import { ReactComponent as BannerTwin } from "assets/img/banner/banner-twin.svg";
import FindPasswordDialog from "components/common/Dialog/FindPassword";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import { EMAIL_REG_EX, PASSWORD_REG_EX } from "data/user.data";
import Cookies from "js-cookie";

function Login() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [loginInfo, setLoginInfo] = useState<LoginInfo>({
    email: "",
    password: "",
  });
  const [isFindPasswordOpen, setIsFindPasswordOpen] = useState<boolean>(false);
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
  });

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginInfo({
      ...loginInfo,
      [name]: value,
    });
  };

  const doLogin = () => {
    setIsLoading(true);
    sendLoginRequest(loginInfo).then(
      (res) => {
        setIsLoading(false);
        const headers = res.headers;
        const XAT = headers["x-auth-token"];
        Cookies.set("X-AUTH-TOKEN", XAT, { domain: ".aibizon.com" });

        const origin = res.data.data.origin;
        Cookies.set("ORIGIN", origin, { domain: ".aibizon.com" });

        navigate("/card");
      },
      (err) => {
        setIsLoading(false);
        setAlertDialogData({ message: "로그인에 실패했습니다." });
        setIsAlertOpen(true);
      }
    );
  };

  const toSignUp = () => {
    navigate("/sign-up");
  };

  return (
    <div
      css={css`
        height: 100vh;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
      `}
    >
      <div
        css={css`
          position: absolute;
          z-index: 0;
          height: 100%;
          width: 100%;
          background-image: url(${BannerBg});
          background-size: cover;
          background-position: center;
        `}
      >
        <BannerCircle
          css={css`
            position: absolute;
            top: -120px;
            left: -400px;
            transform: scale(1.5);
          `}
        />
        <BannerTwin
          css={css`
            position: absolute;
            right: 90px;
            bottom: 90px;
            transform: scale(1.5);
          `}
        />
      </div>
      <div
        css={css`
          padding: 48px;
          box-sizing: border-box;
          background-color: ${PALETTE.light};
          border-radius: 24px;
          display: flex;
          gap: 16px;
          position: relative;
          z-index: 5;
        `}
      >
        <div
          css={css`
            position: absolute;
            top: 24px;
            left: 28px;
          `}
        >
          <LogoSeteukPro
            css={css`
              width: 144px;
              height: fit-content;
            `}
          />
        </div>
        <section
          css={css`
            width: 400px;
            height: 400px;
          `}
        >
          <img
            src={LoginIntro}
            alt="로그인"
            css={css`
              width: 400px;
              height: 400px;
            `}
          />
        </section>
        <section
          css={css`
            min-width: 240px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 28px;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
            `}
          >
            <h1
              css={css`
                font-size: 20px;
              `}
            >
              로그인
            </h1>
          </div>

          <div
            css={css`
              width: 100%;
              display: flex;
              flex-direction: column;
              gap: 16px;
            `}
          >
            <div>
              <BaseInput
                variant="outlined"
                type="text"
                name="email"
                placeholder="이메일주소"
                onInput={handleInput}
                css={css`
                  width: 100%;
                `}
              />
            </div>
            <div>
              <BaseInput
                variant="outlined"
                type="password"
                name="password"
                placeholder="비밀번호"
                onInput={handleInput}
                css={css`
                  width: 100%;
                `}
              />
            </div>
          </div>

          <div
            css={css`
              margin-top: -22px;
              width: 100%;
              height: 22px;
              border-bottom: 1px solid ${PALETTE.border};
            `}
          >
            <span
              css={css`
                display: ${loginInfo.email ? "block" : "none"};
                font-size: 12px;
                color: ${PALETTE.primary};
              `}
            >
              {EMAIL_REG_EX.test(loginInfo.email)
                ? PASSWORD_REG_EX.test(loginInfo.password)
                  ? ""
                  : "영문 대소문자, 숫자, 특수문자 포함 8~20자"
                : "올바른 이메일 주소를 입력해주세요."}
            </span>
          </div>

          <div
            css={css`
              width: 100%;
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              gap: 12px;
            `}
          >
            <BaseButton
              color={PALETTE.primary}
              variant="contained"
              disabled={
                isLoading ||
                !(
                  loginInfo.email &&
                  loginInfo.password &&
                  EMAIL_REG_EX.test(loginInfo.email) &&
                  PASSWORD_REG_EX.test(loginInfo.password)
                )
              }
              css={css`
                padding: 10px;
                font-size: 15px;
                font-weight: 700;
              `}
              onClick={doLogin}
            >
              로그인
            </BaseButton>
            <BaseButton
              color={PALETTE.primary}
              variant="outlined"
              css={css`
                padding: 10px;
                font-size: 15px;
                font-weight: 700;
              `}
              onClick={toSignUp}
            >
              회원가입
            </BaseButton>

            <BaseButton
              color={PALETTE.textSub}
              variant="text"
              css={css`
                width: fit-content;
                padding-left: 0;
                font-weight: 400;
              `}
              onClick={() => setIsFindPasswordOpen(true)}
            >
              *{" "}
              <span
                css={css`
                  font-size: 14px;
                  text-decoration: underline;
                `}
              >
                비밀번호 찾기
              </span>
            </BaseButton>
          </div>
        </section>
      </div>
      <FindPasswordDialog
        isOpen={isFindPasswordOpen}
        setIsOpen={setIsFindPasswordOpen}
      />
      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertDialogData}
      />
    </div>
  );
}

export default Login;
